(() => {
  const SLIDER_TOLERANCE = 16;
  const horizontalSliders = new Set();
  const horizontalBySection = new Map();
  const testimonialBySection = new Map();
  const kollektionAccordionBySection = new Map();
  let globalListenersBound = false;

  const runAllHorizontalUpdates = () => {
    horizontalSliders.forEach((sliderController) => sliderController.update());
  };

  const bindGlobalListeners = () => {
    if (globalListenersBound) return;
    globalListenersBound = true;

    let rafId = 0;
    window.addEventListener('resize', () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        runAllHorizontalUpdates();
      });
    });

    window.addEventListener('load', runAllHorizontalUpdates, { once: true });
  };

  const initHorizontalSlider = (section, config) => {
    if (!section || horizontalBySection.has(section) || section.dataset[config.initFlag] === 'true') return;

    const track = section.querySelector(config.trackSelector);
    if (!track) return;

    const nav = config.navSelector ? section.querySelector(config.navSelector) : null;
    const prevButton = config.prevSelector ? section.querySelector(config.prevSelector) : null;
    const nextButton = config.nextSelector ? section.querySelector(config.nextSelector) : null;
    const progressThumb = config.progressThumbSelector ? section.querySelector(config.progressThumbSelector) : null;
    const progressBar = progressThumb ? progressThumb.closest(config.progressContainerSelector) : null;
    const EDGE_TOLERANCE = 1;

    const getMetrics = () => {
      const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const scrollLeft = Math.max(track.scrollLeft, 0);
      return { maxScroll, scrollLeft };
    };

    const updateNavState = (needsSlider) => {
      if (!prevButton || !nextButton) return;

      if (!needsSlider) {
        prevButton.disabled = true;
        nextButton.disabled = true;
        prevButton.setAttribute('aria-disabled', 'true');
        nextButton.setAttribute('aria-disabled', 'true');
        return;
      }

      const { maxScroll, scrollLeft } = getMetrics();
      const atStart = scrollLeft <= EDGE_TOLERANCE;
      const atEnd = scrollLeft >= (maxScroll - EDGE_TOLERANCE);

      prevButton.disabled = atStart;
      nextButton.disabled = atEnd;
      prevButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    };

    const setVisibility = () => {
      const { maxScroll } = getMetrics();
      const needsSlider = maxScroll > SLIDER_TOLERANCE;

      if (nav) nav.toggleAttribute('hidden', !needsSlider);
      if (progressBar) progressBar.toggleAttribute('hidden', !needsSlider);

      if (!needsSlider && track.scrollLeft !== 0) {
        track.scrollTo({ left: 0, behavior: 'auto' });
      }

      if (typeof config.onVisibilityChange === 'function') {
        config.onVisibilityChange(needsSlider);
      }

      updateNavState(needsSlider);

      return needsSlider;
    };

    const updateProgress = () => {
      if (!progressThumb) {
        setVisibility();
        return;
      }

      if (!setVisibility()) return;

      const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      if (maxScroll <= 1) {
        progressThumb.style.width = '100%';
        progressThumb.style.left = '0';
        progressThumb.style.transform = 'none';
        return;
      }

      const visibleRatio = track.clientWidth / track.scrollWidth;
      const thumbWidth = Math.min(Math.max(visibleRatio * 100, 18), 100);
      const progress = Math.min(Math.max(track.scrollLeft / maxScroll, 0), 1);
      const travel = 100 - thumbWidth;

      progressThumb.style.width = `${thumbWidth}%`;
      progressThumb.style.left = `${travel * progress}%`;
      progressThumb.style.transform = 'none';
    };

    const getStep = () => {
      const firstItem = track.querySelector(config.itemSelector);
      if (!firstItem) return config.fallbackStep;
      const styles = window.getComputedStyle(track);
      const gapValue = parseFloat(styles.columnGap || styles.gap || 0);
      return firstItem.getBoundingClientRect().width + gapValue;
    };

    let scrollRaf = 0;
    const onTrackScroll = () => {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        updateProgress();
      });
    };

    const onPrevClick = () => {
      if (prevButton && prevButton.disabled) return;
      const { scrollLeft } = getMetrics();
      const nextLeft = Math.max(scrollLeft - getStep(), 0);
      track.scrollTo({ left: nextLeft, behavior: 'smooth' });
    };

    const onNextClick = () => {
      if (nextButton && nextButton.disabled) return;
      const { maxScroll, scrollLeft } = getMetrics();
      const nextLeft = Math.min(scrollLeft + getStep(), maxScroll);
      track.scrollTo({ left: nextLeft, behavior: 'smooth' });
    };

    if (prevButton && nextButton) {
      prevButton.addEventListener('click', onPrevClick);
      nextButton.addEventListener('click', onNextClick);
    }

    track.addEventListener('scroll', onTrackScroll, { passive: true });

    const sliderController = {
      update: updateProgress,
      destroy: () => {
        if (prevButton && nextButton) {
          prevButton.removeEventListener('click', onPrevClick);
          nextButton.removeEventListener('click', onNextClick);
        }
        track.removeEventListener('scroll', onTrackScroll);
        if (scrollRaf) {
          window.cancelAnimationFrame(scrollRaf);
          scrollRaf = 0;
        }
        horizontalSliders.delete(sliderController);
        horizontalBySection.delete(section);
        section.dataset[config.initFlag] = 'false';
      }
    };

    horizontalSliders.add(sliderController);
    horizontalBySection.set(section, sliderController);
    bindGlobalListeners();
    updateProgress();

    section.dataset[config.initFlag] = 'true';
  };

  const initTestimonials = (section) => {
    if (!section || testimonialBySection.has(section) || section.dataset.vqTestimonialsInit === 'true') return;

    const slides = Array.from(section.querySelectorAll('[data-vq-testimonial-slide]'));
    const prevButton = section.querySelector('[data-vq-testimonials-prev]');
    const nextButton = section.querySelector('[data-vq-testimonials-next]');
    const progressThumb = section.querySelector('[data-vq-testimonials-progress-thumb]');
    const nav = section.querySelector('.vq-testimonials__nav');
    const progressBar = section.querySelector('.vq-testimonials__progress');

    if (!slides.length) return;

    const hasMultipleSlides = slides.length > 1;
    if (nav) nav.toggleAttribute('hidden', !hasMultipleSlides);
    if (progressBar) progressBar.toggleAttribute('hidden', !hasMultipleSlides);

    let index = slides.findIndex((slide) => slide.getAttribute('data-vq-active') === 'true');
    if (index < 0) index = 0;

    const render = (direction = 'next') => {
      section.setAttribute('data-vq-direction', direction);
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        if (isActive) {
          slide.setAttribute('data-vq-active', 'true');
        } else {
          slide.removeAttribute('data-vq-active');
        }
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (progressThumb) {
        const thumbWidth = 100 / slides.length;
        progressThumb.style.width = `${thumbWidth}%`;
        progressThumb.style.transform = `translateX(${index * 100}%)`;
      }
    };

    const onPrevClick = () => {
      index = (index - 1 + slides.length) % slides.length;
      render('prev');
    };

    const onNextClick = () => {
      index = (index + 1) % slides.length;
      render('next');
    };

    if (prevButton && nextButton && hasMultipleSlides) {
      prevButton.addEventListener('click', onPrevClick);
      nextButton.addEventListener('click', onNextClick);
    }

    render('next');
    section.dataset.vqTestimonialsInit = 'true';

    testimonialBySection.set(section, {
      destroy: () => {
        if (prevButton && nextButton && hasMultipleSlides) {
          prevButton.removeEventListener('click', onPrevClick);
          nextButton.removeEventListener('click', onNextClick);
        }
        testimonialBySection.delete(section);
        section.dataset.vqTestimonialsInit = 'false';
      }
    });
  };

  const initKollektionAccordion = (section) => {
    if (!section) return null;
    if (kollektionAccordionBySection.has(section)) return kollektionAccordionBySection.get(section);
    if (section.dataset.vqKollektionAccordionInit === 'true') return null;

    const content = section.querySelector('[data-vq-kollektion-content]');
    const toggle = section.querySelector('[data-vq-kollektion-toggle]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const mobileOpenDefault = section.dataset.mobileOpenDefault === 'true';

    if (!content || !toggle) return null;

    const syncOpenHeight = () => {
      if (section.classList.contains('is-open') && mobileQuery.matches) {
        content.style.maxHeight = `${content.scrollHeight}px`;
      }
    };

    const updateHeight = (open, instant = false) => {
      if (!mobileQuery.matches) {
        content.style.maxHeight = 'none';
        section.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        return;
      }

      if (instant) {
        content.style.transition = 'none';
      } else {
        content.style.transition = '';
      }

      if (open) {
        section.classList.add('is-open');
        content.style.maxHeight = `${content.scrollHeight}px`;
        toggle.setAttribute('aria-expanded', 'true');
      } else {
        section.classList.remove('is-open');
        content.style.maxHeight = '0px';
        toggle.setAttribute('aria-expanded', 'false');
      }

      if (instant) {
        window.requestAnimationFrame(() => {
          content.style.transition = '';
        });
      }
    };

    const applyResponsiveState = (instant = false) => {
      const shouldBeOpen = mobileQuery.matches ? mobileOpenDefault : true;
      updateHeight(shouldBeOpen, instant);
    };

    const onToggleClick = () => {
      if (!mobileQuery.matches) return;
      updateHeight(!section.classList.contains('is-open'));
    };

    const onResize = () => syncOpenHeight();
    const onMediaChange = () => applyResponsiveState(true);

    toggle.addEventListener('click', onToggleClick);
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onMediaChange);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(onMediaChange);
    }
    window.addEventListener('resize', onResize);

    applyResponsiveState(true);
    section.dataset.vqKollektionAccordionInit = 'true';

    const accordionController = {
      syncOpenHeight,
      destroy: () => {
        toggle.removeEventListener('click', onToggleClick);
        if (mobileQuery.addEventListener) {
          mobileQuery.removeEventListener('change', onMediaChange);
        } else if (mobileQuery.removeListener) {
          mobileQuery.removeListener(onMediaChange);
        }
        window.removeEventListener('resize', onResize);
        kollektionAccordionBySection.delete(section);
        section.dataset.vqKollektionAccordionInit = 'false';
      }
    };

    kollektionAccordionBySection.set(section, accordionController);
    return accordionController;
  };

  const destroyControllersInRoot = (root) => {
    if (!root) return;
    const contains = (section) => root === section || (typeof root.contains === 'function' && root.contains(section));

    for (const [section, controller] of horizontalBySection.entries()) {
      if (!contains(section)) continue;
      controller.destroy();
    }

    for (const [section, controller] of testimonialBySection.entries()) {
      if (!contains(section)) continue;
      controller.destroy();
    }

    for (const [section, controller] of kollektionAccordionBySection.entries()) {
      if (!contains(section)) continue;
      controller.destroy();
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-vq-categories]').forEach((section) => {
      initHorizontalSlider(section, {
        initFlag: 'vqCategoriesSliderInit',
        trackSelector: '.vq-product-categories__list',
        navSelector: '[data-vq-categories-nav]',
        prevSelector: '[data-vq-categories-prev]',
        nextSelector: '[data-vq-categories-next]',
        progressThumbSelector: '[data-vq-categories-progress-thumb]',
        progressContainerSelector: '.vq-product-categories__progress',
        itemSelector: '.vq-product-categories__item',
        fallbackStep: 140
      });
    });

    root.querySelectorAll('[data-vq-products]').forEach((section) => {
      initHorizontalSlider(section, {
        initFlag: 'vqProductsSliderInit',
        trackSelector: '.vq-products__list',
        navSelector: '[data-vq-products-nav]',
        prevSelector: '[data-vq-products-prev]',
        nextSelector: '[data-vq-products-next]',
        progressThumbSelector: '[data-vq-products-progress-thumb]',
        progressContainerSelector: '.vq-products__progress',
        itemSelector: '.vq-products__item',
        fallbackStep: 320
      });
    });

    root.querySelectorAll('[data-vq-rezept]').forEach((section) => {
      initHorizontalSlider(section, {
        initFlag: 'vqRezeptSliderInit',
        trackSelector: '.vq-rezept__list',
        navSelector: '[data-vq-rezept-nav]',
        prevSelector: '[data-vq-rezept-prev]',
        nextSelector: '[data-vq-rezept-next]',
        progressThumbSelector: '[data-vq-rezept-progress-thumb]',
        progressContainerSelector: '.vq-rezept__progress',
        itemSelector: '.vq-rezept__item',
        fallbackStep: 184
      });
    });

    root.querySelectorAll('[data-vq-kollektion]').forEach((section) => {
      const accordionController = initKollektionAccordion(section);
      initHorizontalSlider(section, {
        initFlag: 'vqKollektionSliderInit',
        trackSelector: '.vq-kollektion__grid',
        navSelector: '[data-vq-kollektion-nav]',
        prevSelector: '[data-vq-kollektion-prev]',
        nextSelector: '[data-vq-kollektion-next]',
        progressThumbSelector: '[data-vq-kollektion-progress-thumb]',
        progressContainerSelector: '.vq-kollektion__progress',
        itemSelector: '.vq-kollektion__item',
        fallbackStep: 248,
        onVisibilityChange: () => {
          if (accordionController && typeof accordionController.syncOpenHeight === 'function') {
            accordionController.syncOpenHeight();
          }
        }
      });
    });

    root.querySelectorAll('[data-vq-testimonials]').forEach(initTestimonials);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyControllersInRoot(event.target);
  });
})();
